import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import {
  Target,
  Plus,
  ChevronDown,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Building,
  MessageSquare,
  MessageCircle,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ArrowRight,
  Loader2,
  History,
} from 'lucide-react';
import api from '@/lib/api';

const CONTACT_ICONS = {
  telephone: Phone,
  email: Mail,
  visite_client: MapPin,
  visite_showroom: Building,
  sms: MessageSquare,
  whatsapp: MessageCircle,
};

const ClientOpportunityTracker = ({ clientId, clientName }) => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [expandedOpp, setExpandedOpp] = useState(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);
  
  const [createForm, setCreateForm] = useState({
    title: '',
    estimated_amount: '',
    source: '',
    notes: '',
  });
  
  const [activityForm, setActivityForm] = useState({
    contact_type: '',
    contact_result: '',
    notes: '',
    next_action_date: '',
    new_stage: '',
  });
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetchOpportunities();
  }, [clientId]);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/opportunities/config');
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchOpportunities = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/opportunities/client/${clientId}`);
      setOpportunities(response.data.opportunities || []);
      // Auto-expand first open opportunity
      const firstOpen = response.data.opportunities?.find(o => o.status === 'open');
      if (firstOpen) {
        setExpandedOpp(firstOpen.id);
      }
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOpportunity = async () => {
    setSaving(true);
    try {
      await api.post('/opportunities/', {
        client_id: clientId,
        title: createForm.title || `Opportunité - ${clientName}`,
        estimated_amount: createForm.estimated_amount ? parseFloat(createForm.estimated_amount) : null,
        source: createForm.source || null,
        notes: createForm.notes || null,
      });
      toast.success('Opportunité créée');
      setCreateDialogOpen(false);
      setCreateForm({ title: '', estimated_amount: '', source: '', notes: '' });
      fetchOpportunities();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleChecklistChange = async (oppId, itemId, checked) => {
    try {
      await api.post(`/opportunities/${oppId}/checklist`, {
        item_id: itemId,
        checked: checked,
      });
      fetchOpportunities();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const openActivityDialog = (opp, isStageChange = false) => {
    setSelectedOpp(opp);
    const nextStage = isStageChange ? getNextStage(opp.stage)?.id || '' : '';
    setActivityForm({
      contact_type: '',
      contact_result: '',
      notes: '',
      next_action_date: '',
      new_stage: nextStage,
    });
    setActivityDialogOpen(true);
  };

  const getNextStage = (currentStage) => {
    if (!config) return null;
    const currentIndex = config.stages.findIndex(s => s.id === currentStage);
    if (currentIndex < config.stages.length - 2) { // -2 to exclude "cloture"
      return config.stages[currentIndex + 1];
    }
    return null;
  };

  const handleLogActivity = async () => {
    if (!activityForm.contact_type || !activityForm.contact_result) {
      toast.error('Veuillez remplir le type et le résultat du contact');
      return;
    }

    setSaving(true);
    try {
      if (activityForm.new_stage) {
        await api.patch(`/opportunities/${selectedOpp.id}/stage`, {
          new_stage: activityForm.new_stage,
          contact_type: activityForm.contact_type,
          contact_result: activityForm.contact_result,
          notes: activityForm.notes,
          next_action_date: activityForm.next_action_date || null,
        });
        toast.success('Étape changée avec succès');
      } else {
        await api.post(`/opportunities/${selectedOpp.id}/log-activity`, {
          contact_type: activityForm.contact_type,
          contact_result: activityForm.contact_result,
          notes: activityForm.notes,
          next_action_date: activityForm.next_action_date || null,
        });
        toast.success('Activité enregistrée');
      }
      
      setActivityDialogOpen(false);
      fetchOpportunities();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const formatDelay = (hours) => {
    if (hours < 24) return `${hours}h de retard`;
    return `${Math.round(hours / 24)}j de retard`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading || !config) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="w-5 h-5 text-orange-500" />
            Suivi Commercial
          </CardTitle>
          <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Nouvelle opportunité
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {opportunities.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Target className="w-10 h-10 mx-auto mb-3 opacity-20" />
            <p>Aucune opportunité en cours</p>
            <Button 
              variant="link" 
              className="mt-2 text-orange-500"
              onClick={() => setCreateDialogOpen(true)}
            >
              Créer une opportunité
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {opportunities.map((opp) => (
              <Collapsible
                key={opp.id}
                open={expandedOpp === opp.id}
                onOpenChange={(open) => setExpandedOpp(open ? opp.id : null)}
              >
                <div className={`border rounded-lg ${opp.is_overdue ? 'border-red-200 bg-red-50' : 'border-slate-200'}`}>
                  <CollapsibleTrigger className="w-full">
                    <div className="p-4">
                      {/* Header with progress */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {expandedOpp === opp.id ? (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          )}
                          <span className="font-medium text-slate-800">{opp.title}</span>
                          {opp.status === 'won' && (
                            <Badge className="bg-green-100 text-green-700">Gagné</Badge>
                          )}
                          {opp.status === 'lost' && (
                            <Badge variant="secondary">Perdu</Badge>
                          )}
                          {opp.is_overdue && (
                            <Badge variant="destructive" className="text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              {formatDelay(opp.overdue_hours)}
                            </Badge>
                          )}
                        </div>
                        <span className="text-sm font-medium text-slate-600">{opp.progress}%</span>
                      </div>
                      
                      {/* Progress bar */}
                      <Progress value={opp.progress} className="h-2 mb-3" />
                      
                      {/* Stage indicators */}
                      <div className="flex items-center gap-1 overflow-x-auto pb-1">
                        {config.stages.slice(0, -1).map((stage, index) => {
                          const isCurrent = opp.stage === stage.id;
                          const isPast = config.stages.findIndex(s => s.id === opp.stage) > index;
                          
                          return (
                            <div
                              key={stage.id}
                              className={`flex-shrink-0 px-2 py-1 rounded text-xs font-medium transition-all ${
                                isCurrent 
                                  ? 'text-white' 
                                  : isPast 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-slate-100 text-slate-400'
                              }`}
                              style={isCurrent ? { backgroundColor: stage.color } : {}}
                            >
                              {isPast && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                              {stage.label}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="px-4 pb-4 border-t">
                      {/* Current stage info */}
                      <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: `${opp.stage_config?.color}15` }}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium" style={{ color: opp.stage_config?.color }}>
                              {opp.stage_config?.label}
                            </p>
                            {opp.stage_config?.delay_label && (
                              <p className="text-sm text-slate-500 mt-1">
                                <Clock className="w-3 h-3 inline mr-1" />
                                {opp.stage_config.delay_label}
                              </p>
                            )}
                          </div>
                          {opp.status === 'open' && (
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => openActivityDialog(opp)}>
                                <Phone className="w-4 h-4 mr-1" />
                                Contact
                              </Button>
                              {getNextStage(opp.stage) && (
                                <Button 
                                  size="sm" 
                                  className="bg-orange-500 hover:bg-orange-600"
                                  onClick={() => openActivityDialog(opp, true)}
                                >
                                  <ArrowRight className="w-4 h-4 mr-1" />
                                  Avancer
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Checklist */}
                      {opp.stage_config?.checklist?.length > 0 && opp.status === 'open' && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-slate-600 mb-2">Checklist</p>
                          <div className="space-y-2">
                            {opp.stage_config.checklist.map((item) => {
                              const isChecked = opp.checklist?.[opp.stage]?.[item.id] || false;
                              return (
                                <div key={item.id} className="flex items-center gap-2">
                                  <Checkbox
                                    id={`${opp.id}-${item.id}`}
                                    checked={isChecked}
                                    onCheckedChange={(checked) => handleChecklistChange(opp.id, item.id, checked)}
                                  />
                                  <label
                                    htmlFor={`${opp.id}-${item.id}`}
                                    className={`text-sm ${isChecked ? 'text-slate-400 line-through' : 'text-slate-700'}`}
                                  >
                                    {item.label}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Recent activities */}
                      {opp.activities?.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-1">
                            <History className="w-4 h-4" />
                            Dernières activités
                          </p>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {opp.activities.slice(-5).reverse().map((activity) => {
                              const ContactIcon = CONTACT_ICONS[activity.contact_type] || Phone;
                              return (
                                <div key={activity.id} className="text-sm p-2 bg-slate-50 rounded">
                                  <div className="flex items-center gap-2">
                                    <ContactIcon className="w-3 h-3 text-slate-400" />
                                    <span className="font-medium text-slate-700">
                                      {activity.type === 'stage_change' ? 'Passage étape' : 'Contact'}
                                    </span>
                                    <span className="text-slate-400">•</span>
                                    <span className="text-slate-500">{formatDate(activity.created_at)}</span>
                                  </div>
                                  {activity.notes && (
                                    <p className="text-slate-500 mt-1 pl-5">{activity.notes}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))}
          </div>
        )}

        {/* Create Opportunity Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouvelle opportunité</DialogTitle>
              <DialogDescription>
                Créer une opportunité pour {clientName}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Titre (optionnel)</Label>
                <Input
                  value={createForm.title}
                  onChange={(e) => setCreateForm(f => ({ ...f, title: e.target.value }))}
                  placeholder={`Opportunité - ${clientName}`}
                />
              </div>
              <div className="space-y-2">
                <Label>Montant estimé (€)</Label>
                <Input
                  type="number"
                  value={createForm.estimated_amount}
                  onChange={(e) => setCreateForm(f => ({ ...f, estimated_amount: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Source</Label>
                <Select 
                  value={createForm.source}
                  onValueChange={(v) => setCreateForm(f => ({ ...f, source: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="D'où vient ce contact ?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="web">Site web</SelectItem>
                    <SelectItem value="telephone">Appel téléphonique</SelectItem>
                    <SelectItem value="salon">Salon / Événement</SelectItem>
                    <SelectItem value="parrainage">Parrainage</SelectItem>
                    <SelectItem value="reseaux_sociaux">Réseaux sociaux</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={createForm.notes}
                  onChange={(e) => setCreateForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Notes sur cette opportunité..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleCreateOpportunity} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Créer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Activity Dialog */}
        <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {activityForm.new_stage ? 'Avancer l\'opportunité' : 'Enregistrer un contact'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {activityForm.new_stage && config && (
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-sm text-orange-700">
                    <ArrowRight className="w-4 h-4 inline mr-1" />
                    Passage à l'étape: <strong>{config.stages.find(s => s.id === activityForm.new_stage)?.label}</strong>
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Type de contact *</Label>
                <Select 
                  value={activityForm.contact_type}
                  onValueChange={(v) => setActivityForm(f => ({ ...f, contact_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.contact_types.map((type) => {
                      const Icon = CONTACT_ICONS[type.id] || Phone;
                      return (
                        <SelectItem key={type.id} value={type.id}>
                          <span className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            {type.label}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Résultat *</Label>
                <Select 
                  value={activityForm.contact_result}
                  onValueChange={(v) => setActivityForm(f => ({ ...f, contact_result: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.contact_results.map((result) => (
                      <SelectItem key={result.id} value={result.id}>
                        {result.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Prochaine action</Label>
                <Input
                  type="date"
                  value={activityForm.next_action_date}
                  onChange={(e) => setActivityForm(f => ({ ...f, next_action_date: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={activityForm.notes}
                  onChange={(e) => setActivityForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Notes sur l'échange..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setActivityDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleLogActivity} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ClientOpportunityTracker;
